import { AdgroupFormData, BEHAVIOR_TARGETING_SCENES_TYPE, CampaignFormData } from '@tt4b/ttam-enums';
import { cloneDeep } from 'lodash-es';
import { StateEnum, useBehaviorListApi, useRequest, useStore } from '@tt4b/ttam-hooks';
import { adgroupFormData, getIdItemMap, getIdLabelMap, setParent } from '@tt4b/ttam-utils';
import { computed, nextTick, onUnmounted, Ref, ref, watch } from '@vue/composition-api';
import { i18n } from '@tt4b/ttam-i18n';

const VideoCategoryDeprecatedId = 9;
const videoBehaviorList = ref([]);
const creatorBehaviorList = ref([]);

const { state } = useStore();
const creatorBehaviorMap = computed(() => state[StateEnum.targetInfo].value.creatorBehaviorMap);
const videoBehaviorMap = computed(() => state[StateEnum.targetInfo].value.videoBehaviorMap);
const hashtagBehaviorMap = computed(() => state[StateEnum.targetInfo].value.hashtagBehaviorMap);

export function useBehaviorOptions(options: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<adgroupFormData>;
}) {
  const { campaignData, adgroupData } = options;
  const { run, cancel, isLoading } = useRequest(useBehaviorListApi, null, { manual: true });

  const parmas = computed(() => ({
    inventory: adgroupData.value.inventoryFlow,
    industryTypes: campaignData.value.industryTypes,
  }));

  async function loadBehaviorOptions() {
    cancel();
    if (isLoading.value) {
      return;
    }
    const data = await run(parmas.value);
    const { videoRelated, creatorRelated } = data || {};
    if (videoRelated) {
      videoRelated.forEach(item => (item.isDeprecated = item.id === VideoCategoryDeprecatedId));
      setParent({ children: videoRelated });
      videoBehaviorList.value = videoRelated;
      state[StateEnum.targetInfo].value.videoBehaviorMap = getIdLabelMap(videoBehaviorList.value);
    }
    if (creatorRelated) {
      setParent({ children: creatorRelated });
      creatorBehaviorList.value = creatorRelated;
      state[StateEnum.targetInfo].value.creatorBehaviorMap = getIdLabelMap(creatorBehaviorList.value);
    }
  }

  watch(
    parmas,
    () => {
      loadBehaviorOptions();
    },
    {
      immediate: true,
    },
  );

  onUnmounted(() => {
    videoBehaviorList.value = [];
    creatorBehaviorList.value = [];
  });

  return {
    loadBehaviorOptions,
    getData: run,
    /** video 视频类标签列表 */
    videoBehaviorList,
    /** video 视频类标签 id 为 key, label 为 value 的 Map */
    videoBehaviorMap,
    /** creator 创作者类标签列表 */
    creatorBehaviorList,
    /** creator 创作者类标签 id 为 key 的 Map */
    creatorBehaviorMap,
  };
}

export const HASHTAG_TARGETING_CONSTANT_FIELDS = {
  actions: [6],
  day: 0,
};

/** 行为定向相关数据处理逻辑 */
export function useBehaviorData(options: { adgroupData: Ref<adgroupFormData> }) {
  const { adgroupData } = options;
  const formData = computed(() => adgroupData.value);

  const actionScenesV2 = computed(() => formData.value.actionScenesV2 ?? []);
  const actionDaysV2 = computed(() => formData.value.actionDaysV2 ?? []);
  const videoActionsV2 = computed(() => formData.value.videoActionsV2 ?? []);
  const actionCategoriesV2 = computed(() => formData.value.actionCategoriesV2 ?? []);

  const videoIndex = computed(() => actionScenesV2.value.indexOf(BEHAVIOR_TARGETING_SCENES_TYPE.video));
  const creatorIndex = computed(() => actionScenesV2.value.indexOf(BEHAVIOR_TARGETING_SCENES_TYPE.creator));
  const hashtagIndex = computed(() => actionScenesV2.value.indexOf(BEHAVIOR_TARGETING_SCENES_TYPE.hashtag));

  const videoData = computed(() => {
    const index = videoIndex.value;

    return {
      day: actionDaysV2?.value?.[index] ?? 0,
      actions: videoActionsV2?.value?.[index] ?? [],
      categories: actionCategoriesV2?.value?.[index] ?? [],
    };
  });

  const creatorData = computed(() => {
    const index = creatorIndex.value;

    return {
      day: actionDaysV2?.value?.[index] ?? 0,
      actions: videoActionsV2?.value?.[index] ?? [],
      categories: actionCategoriesV2?.value?.[index] ?? [],
    };
  });

  const hashtagData = computed(() => {
    const index = hashtagIndex.value;

    return {
      ...cloneDeep(HASHTAG_TARGETING_CONSTANT_FIELDS),
      categories: actionCategoriesV2?.value?.[index] ?? [],
    };
  });

  const videoDay = computed(() => videoData.value.day);

  const videoActions = computed(() => videoData.value.actions);

  const videoCategories = computed(() => videoData.value.categories);

  const creatorActions = computed(() => creatorData.value.actions);

  const creatorCategories = computed(() => creatorData.value.categories);

  const hashtagCategories = computed(() => hashtagData.value.categories);

  function changeData(data, type: 'video' | 'creator' | 'hashtag') {
    const curScene = BEHAVIOR_TARGETING_SCENES_TYPE[type];
    const {
      actionScenesV2: actionScenesV2Temp,
      actionDaysV2: actionDaysV2Temp,
      videoActionsV2: videoActionsV2Temp,
      actionCategoriesV2: actionCategoriesV2Temp,
    } = cloneDeep(formData.value);
    const asV2 = actionScenesV2Temp || [];
    const adV2 = actionDaysV2Temp || [];
    const vaV2 = videoActionsV2Temp || [];
    const acV2 = actionCategoriesV2Temp || [];
    const index = asV2.indexOf(curScene);
    const { day, categories, actions } = data;
    const isHasOperated = day || categories?.length || actions?.length;
    if (index > -1) {
      if (!isHasOperated) {
        asV2.splice(index, 1);
        adV2.splice(index, 1);
        vaV2.splice(index, 1);
        acV2.splice(index, 1);
      } else {
        adV2[index] = day;
        vaV2[index] = actions;
        acV2[index] = categories;
      }
    } else {
      asV2.push(curScene);
      adV2.push(day);
      vaV2.push(actions);
      acV2.push(categories);
    }
    return {
      actionDaysV2: adV2,
      actionScenesV2: asV2,
      videoActionsV2: vaV2,
      actionCategoriesV2: acV2,
    };
  }

  function getClearDataByType(type: 'video' | 'creator' | 'hashtag') {
    const curScene = BEHAVIOR_TARGETING_SCENES_TYPE[type];
    if (formData.value.actionScenesV2.indexOf(curScene) > -1) {
      // 有数据时才清空
      return changeData({ day: 0, categories: [], actions: [] }, type);
    }
    return {};
  }
  function changeHashtagCategories(hashtags) {
    return changeData(
      { ...hashtagData.value, ...cloneDeep(HASHTAG_TARGETING_CONSTANT_FIELDS), categories: hashtags },
      'hashtag',
    );
  }
  return {
    videoDay,
    videoActions,
    videoCategories,

    creatorActions,
    creatorCategories,

    hashtagCategories,

    videoData,
    creatorData,
    hashtagData,
    getChangedBehaviorData: changeData,
    getClearDataByType,
    changeHashtagCategories,
  };
}

export function useBehaviorLabel(options: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = options;
  const { creatorCategories, videoCategories, hashtagCategories } = useBehaviorData({
    adgroupData,
  });

  const creatorBehaviorLabel = computed(() => {
    if (!creatorCategories.value.length) {
      return i18n.t('adgroup_no_limit') as string;
    }

    return creatorCategories.value.map(item => creatorBehaviorMap.value[item]);
  });

  const videoBehaviorLabel = computed(() => {
    if (!videoCategories.value.length) {
      return i18n.t('adgroup_no_limit') as string;
    }

    return videoCategories.value.map(item => videoBehaviorMap.value[item]);
  });

  const hashtagBehaviorLabel = computed(() => {
    if (!hashtagCategories.value.length) {
      return i18n.t('adgroup_no_limit') as string;
    }

    return hashtagCategories.value.map(item => hashtagBehaviorMap.value?.find(e => e.id === item)?.label);
  });

  return {
    creatorBehaviorLabel,
    videoBehaviorLabel,
    hashtagBehaviorLabel,
  };
}

/** 行为定向通用校验 */
export function useValidateBehavior() {
  const dayEle = ref();
  const actionsEle = ref();
  const categoriesEle = ref();

  function validate() {
    nextTick(() => {
      dayEle.value && dayEle.value.validate('custom');
      actionsEle.value && actionsEle.value.validate('custom');
      categoriesEle.value && categoriesEle.value.validate('custom');
    });
  }

  return {
    validate,
    dayEle,
    actionsEle,
    categoriesEle,
  };
}
